import { useEffect, useRef } from 'react';

import type { ScaleBand } from 'd3-scale';
import type { FairValueHistogramBucket } from '../fairValueHistogramFragment';
import type { Instance } from '@popperjs/core';
import {
  applyStyles,
  computeStyles,
  preventOverflow,
  popperOffsets,
  eventListeners,
  popperGenerator,
} from '@popperjs/core';
import type { CurrencyISO } from '@/constants/currencies';
import { formatterCurrencyTruncated } from '@/utilities/format';
import { isNumber } from 'lodash';
import { MARKER_COLORS_CSS_VARS } from '../utils';
import { cn } from '@simplywallst/ui-core/next';
import { FairValueHistogramTooltipRow } from './FairValueHistogramTooltipRow';

const createPopper = popperGenerator({
  defaultOptions: {
    placement: 'top',
    strategy: 'absolute',
  },
  defaultModifiers: [
    {
      ...eventListeners,
      options: {
        scroll: true,
        resize: true,
      },
    },
    {
      ...computeStyles,
      options: {
        gpuAcceleration: false,
      },
    },
    applyStyles,
    popperOffsets,
    {
      ...preventOverflow,
      options: {
        scroll: true,
      },
    },
  ],
});

type Props = {
  selectedBucket: FairValueHistogramBucket | null;
  xBandScale: ScaleBand<number>;
  containerElement: HTMLElement | null;
  currencyIso: CurrencyISO;
  fairValue: number | null | undefined;
  sharePrice: number | null | undefined;
};

export const FairValueHistogramTooltip = ({
  selectedBucket,
  xBandScale,
  containerElement,
  currencyIso,
  fairValue,
  sharePrice,
}: Props) => {
  const popperRef = useRef<Instance | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!selectedBucket) return;

    const bucketX = xBandScale(selectedBucket.min) ?? 0;
    const bucketWidth = xBandScale.bandwidth();

    const initialisePopper = () => {
      if (!tooltipRef.current || !containerElement) {
        return;
      }

      const containerRect = containerElement.getBoundingClientRect();
      // Create virtual element at center of selected bar
      const x = containerRect.x + bucketX + bucketWidth / 2;
      const y = containerRect.y;
      const virtualElement = {
        getBoundingClientRect: () =>
          ({
            width: 0,
            height: 0,
            x,
            y,
            top: y,
            right: x,
            bottom: y,
            left: x,
          }) as DOMRect,
      };

      if (popperRef.current) {
        popperRef.current.destroy();
      }
      popperRef.current = createPopper(virtualElement, tooltipRef.current, {
        modifiers: [
          {
            ...preventOverflow,
            options: {
              boundary: containerElement,
            },
          },
        ],
      });
    };

    window.addEventListener('scroll', initialisePopper, true);

    initialisePopper();

    return () => {
      window.removeEventListener('scroll', initialisePopper, true);
      if (popperRef.current) {
        popperRef.current.destroy();
        popperRef.current = null;
      }
    };
  }, [selectedBucket, xBandScale, containerElement]);

  return (
    <div
      ref={tooltipRef}
      className={cn(selectedBucket ? 'absolute' : 'sr-only')}
      aria-live="polite"
      aria-hidden={!selectedBucket}
    >
      <div role="tooltip">
        <div
          className={cn(
            'grid grid-cols-[max-content_1fr] gap-x0_5 gap-x-x2 rounded-md bg-surface p-x1 text-tiny text-solid',
            selectedBucket
              ? 'opacity-100 transition-opacity duration-500'
              : 'opacity-0'
          )}
        >
          <>
            <div className="text-tiny text-soft">Valuation Range</div>
            <div className="text-right text-tiny">
              {formatterCurrencyTruncated(
                selectedBucket?.min ?? 0,
                currencyIso,
                2
              )}{' '}
              -{' '}
              {formatterCurrencyTruncated(
                selectedBucket?.max ?? 0,
                currencyIso,
                2
              )}
            </div>
          </>
          <>
            <div className="text-tiny text-soft">Fair Values</div>
            <div className="text-right text-tiny">
              {selectedBucket?.count ?? 0}
            </div>
          </>

          {isNumber(fairValue) && (
            <FairValueHistogramTooltipRow
              label="My Fair Value"
              color={MARKER_COLORS_CSS_VARS.MY_FAIR_VALUE}
            >
              {formatterCurrencyTruncated(fairValue, currencyIso, 2)}
            </FairValueHistogramTooltipRow>
          )}
          {isNumber(sharePrice) && (
            <FairValueHistogramTooltipRow
              label="Last Price"
              color={MARKER_COLORS_CSS_VARS.SHARE_PRICE}
            >
              {formatterCurrencyTruncated(sharePrice, currencyIso, 2)}
            </FairValueHistogramTooltipRow>
          )}
        </div>
      </div>
    </div>
  );
};
